import "./NewInstrumentsPage.css"
import React, {FunctionComponent, useEffect, useMemo, useState} from "react"
import {InstrumentTableRow} from "../../../data/services/instrument-table-service";
import InstrumentViewer from "../components/instrumentviewer/InstrumentViewer";
import {ObjectsTable} from "../components/objectstable/ObjectsTable";
import {getInstrumentRows} from "./NewInstrumentsPageSvc";
import InstrumentUI from "../components/Instrumentui/InstrumentUI";
import StyledSelect from "../components/styledselect/StyledSelect";
import PageHeader from "../components/pageheader/PageHeader";
import InfoItem from "../components/infoitem/InfoItem";

const txt_title = "Instruments";
const txt_subtitle = "Monitor your sequencing fleet";
export const FILTER_ALL = "ALL" as const;
type TypeFilter = typeof FILTER_ALL | string;
type SortKey = "name" | "serial" | "instrumentType" | "state";
const txt_filterandsort = "Filter and Sort";
const txt_sortname = "Sort by name";
const txt_sortserial = "Sort by serial";
const txt_sorttype = "Sort by type";
const txt_sortstate = "Sort by state";

interface TableHeaderProps {
    sortKey: SortKey;
    setSortKey: React.Dispatch<React.SetStateAction<SortKey>>;
    typeFilter: TypeFilter;
    setTypeFilter: React.Dispatch<React.SetStateAction<TypeFilter>>;
    typeOptions: string[];
}

const requiredFields: (keyof InstrumentTableRow)[] = [
    "serial",
    "name",
    "instrumentType",
    "state",
];

const TableHeader = ({sortKey, setSortKey, typeFilter, setTypeFilter, typeOptions}: TableHeaderProps) => {
    return (
        <div className="newinstruments-table-header">
            <StyledSelect
                style={{width: "220px"}}
                value={typeFilter}
                onChange={(e) =>
                    setTypeFilter(e.target.value === FILTER_ALL ? FILTER_ALL : e.target.value)}
            >
                <option value={FILTER_ALL}>All types</option>
                {typeOptions.map((type) => (
                    <option key={type} value={type}>
                        {type}
                    </option>
                ))}
            </StyledSelect>
            <StyledSelect style={{width: "220px"}}
                          value={sortKey}
                          onChange={(e) => setSortKey(e.target.value as SortKey)}
            >
                <option value="name">{txt_sortname}</option>
                <option value="serial">{txt_sortserial}</option>
                <option value="instrumentType">{txt_sorttype}</option>
                <option value="state">{txt_sortstate}</option>
            </StyledSelect>
        </div>
    );
}

export const NewInstrumentsPage: FunctionComponent = () => {
    const [sortKey, setSortKey] = useState<SortKey>("name");
    const [typeFilter, setTypeFilter] = useState<TypeFilter>(FILTER_ALL);
    const [selectedSerial, setSelectedSerial] = useState<string | null>(null);

    const {rows, error} = getInstrumentRows();
    if (error) return <div>Error: {error}</div>;

    const validRows = rows.filter(row =>
        requiredFields.every(field => row[field] !== null)
    );

    const typeOptions = useMemo(() => {
        const set = new Set<string>();
        validRows.forEach((row) => {
            set.add(String(row.instrumentType));
        });
        return Array.from(set).sort();
    }, [validRows]);

    // 🔹 Filter by type, then sort
    const visibleRows = useMemo(() => {
        const filtered =
            typeFilter === FILTER_ALL
                ? validRows
                : validRows.filter(
                    (row) => String(row.instrumentType) === String(typeFilter)
                );

        return [...filtered].sort((a, b) => {
            const av = String(a[sortKey] ?? "");
            const bv = String(b[sortKey] ?? "");
            return av.localeCompare(bv);
        });
    }, [validRows, typeFilter, sortKey]);

    const sortedRows = useMemo(() => {
        return [...rows].sort((a, b) => {
            const av = String(a[sortKey] ?? "");
            const bv = String(b[sortKey] ?? "");
            return av.localeCompare(bv);
        });
    }, [rows, sortKey]);

    const selectedItem = sortedRows.find(
        (r) => r.serial === selectedSerial
    ) ?? null;

    useEffect(() => {
        if (visibleRows.length > 0) {
            setSelectedSerial((prev) => prev ?? visibleRows[0].serial);
        }
    }, [visibleRows]);

    const InstrumentsInfo: React.FC = () => {
        return (
            <div className="info-item-container">
                <InfoItem title="1 instrument  running" dotcolor="var(--blue-medium-color)"/>
                <InfoItem title="3 instruments online" dotcolor="var(--green-bright-color)"/>
                <InfoItem title="1 instrument  offline" dotcolor="var(--gray-light-color)"/>
                <InfoItem title="4 instruments total" dotcolor="var(--white-almost-color)"/>
            </div>
        );
    }

    return (
        <div className="newinstruments-container">
            <PageHeader title={txt_title} subtitle={txt_subtitle} info={<InstrumentsInfo/>}/>
            <div className="newinstruments">
                <div className="newinstruments-left">
                    <div className="newinstruments-table-header-text">{txt_filterandsort}</div>
                    <div className="newinstruments-instruments">
                        <ObjectsTable<InstrumentTableRow>
                            items={visibleRows}
                            getKey={(row) => row.serial}
                            selectedKey={selectedSerial}
                            onRowClick={(row) => setSelectedSerial(row.serial)}
                            header={
                                <TableHeader sortKey={sortKey}
                                             setSortKey={setSortKey}
                                             typeFilter={typeFilter}
                                             setTypeFilter={setTypeFilter}
                                             typeOptions={typeOptions}/>
                            }
                            renderRow={(row, {isSelected, isHovered}) => (
                                <InstrumentUI row={row}
                                              clicked={() => setSelectedSerial(row.serial)}
                                              isSelected={isSelected}
                                              isHovered={isHovered}
                                              isSelectedAndHovered={isSelected && isHovered}
                                />
                            )}
                        />
                    </div>
                </div>
                <div className="newinstruments-right">
                    {selectedItem &&
                        <InstrumentViewer instrument={selectedItem}/>
                    }
                </div>
            </div>
        </div>
    );
}
